import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { useOrganizationAnalytics } from '@/hooks/useOrganizationAnalytics';
import { BarChart3, TrendingUp, TrendingDown, Users, Package, DollarSign, Activity } from 'lucide-react';

interface AnalyticsDashboardProps {
  organizationId?: string;
}

export const AnalyticsDashboard = ({ organizationId }: AnalyticsDashboardProps = {}) => {
  const { analytics, loading } = useOrganizationAnalytics(organizationId);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid md:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="space-y-2">
                <Skeleton className="h-4 w-[100px]" />
                <Skeleton className="h-3 w-[80px]" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-[60px] mb-2" />
                <Skeleton className="h-3 w-[120px]" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Analytics</CardTitle>
          <CardDescription>Unable to load analytics data</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const formatChange = (change: number) => {
    const isPositive = change > 0;
    const Icon = isPositive ? TrendingUp : TrendingDown;
    const color = isPositive ? 'text-green-600' : 'text-red-600';
    
    return (
      <p className={`text-sm ${color} flex items-center`}>
        <Icon className="h-3 w-3 mr-1" />
        {Math.abs(change).toFixed(1)}% from last month
      </p>
    );
  };

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              <Package className="h-4 w-4 mr-2" />
              Total Bookings
            </CardTitle>
            <CardDescription>This month</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">{analytics.totalBookings}</div>
            {formatChange(analytics.totalBookingsChange)}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              <Users className="h-4 w-4 mr-2" />
              Active Members
            </CardTitle>
            <CardDescription>Organization members</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">{analytics.activeMembers}</div>
            <p className="text-sm text-muted-foreground">
              {analytics.activeMembers === 1 ? 'member' : 'members'} total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              <Activity className="h-4 w-4 mr-2" />
              Completed Bookings
            </CardTitle>
            <CardDescription>Successful deliveries</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">{analytics.completedBookings}</div>
            {formatChange(analytics.completedBookingsChange)}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              <DollarSign className="h-4 w-4 mr-2" />
              Revenue Generated
            </CardTitle>
            <CardDescription>Total confirmed payments</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">
              R{analytics.revenueGenerated.toLocaleString()}
            </div>
            <p className="text-sm text-muted-foreground">
              From confirmed quotes
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Bookings by Status */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <BarChart3 className="h-5 w-5 mr-2" />
              Bookings by Status
            </CardTitle>
            <CardDescription>Current month breakdown</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {analytics.bookingsByStatus.map((status) => (
                <div key={status.status} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Badge variant="outline" className="capitalize">
                        {status.status.replace('_', ' ')}
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        {status.count} booking{status.count !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <span className="text-sm font-medium">
                      {status.percentage.toFixed(1)}%
                    </span>
                  </div>
                  <Progress value={status.percentage} className="h-2" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Top Users */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Users className="h-5 w-5 mr-2" />
              Top Users
            </CardTitle>
            <CardDescription>Most active customers</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics.topUsers.slice(0, 5).map((user, index) => (
                <div key={user.email} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs font-medium">
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium">{user.name}</p>
                      <p className="text-sm text-muted-foreground">{user.email}</p>
                    </div>
                  </div>
                  <Badge variant="secondary">
                    {user.bookings} booking{user.bookings !== 1 ? 's' : ''}
                  </Badge>
                </div>
              ))}
              
              {analytics.topUsers.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No user activity this month
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Trend */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <TrendingUp className="h-5 w-5 mr-2" />
            Booking Trends
          </CardTitle>
          <CardDescription>Daily bookings over the last 30 days</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span>Peak day: {analytics.bookingsTrend.reduce((max, day) => 
                day.count > max.count ? day : max
              ).count} bookings</span>
              <span>Average: {(analytics.bookingsTrend.reduce((sum, day) => 
                sum + day.count, 0) / analytics.bookingsTrend.length).toFixed(1)} bookings/day</span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-primary/20 to-primary flex">
                {analytics.bookingsTrend.map((day, index) => {
                  const maxCount = Math.max(...analytics.bookingsTrend.map(d => d.count));
                  const height = maxCount > 0 ? (day.count / maxCount) * 100 : 0;
                  return (
                    <div
                      key={index}
                      className="flex-1 bg-primary/60 self-end"
                      style={{ height: `${height}%` }}
                      title={`${day.date}: ${day.count} bookings`}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};